import React from 'react';
import { AbsoluteFill, useCurrentFrame, useVideoConfig, interpolate, spring } from 'remotion';

/**
 * #296 CSS Scroll-Driven Animations
 *
 * 2026 CSS 新特性演示
 * - 模拟 scroll-driven 动画效果
 * - 时间轴驱动的滚动动画
 * - 视差层叠效果
 * - 进度条动画
 *
 * 技术要点：
 * - 模拟 scroll-timeline CSS 特性
 * - interpolate 函数实现关键帧动画
 * - Spring 物理动画
 * - 多层视差效果
 */

export type ScrollDrivenCard = {
  title: string;
  desc: string;
  delay: number;
};

export type ScrollDrivenAnimationProps = {
  /** 主标题 */
  title?: string;
  /** 副标题 */
  subtitle?: string;
  /** 卡片列表 */
  cards?: ScrollDrivenCard[];
  /** 滚动指示器文字 */
  scrollHintText?: string;
  /** 背景渐变（CSS background 值） */
  background?: string;
  /** 进度条渐变（CSS background 值） */
  progressBarGradient?: string;
  /** 标题渐变（CSS background 值） */
  titleGradient?: string;
  /** 主文字颜色 */
  primaryColor?: string;
  /** 次要文字颜色 */
  secondaryColor?: string;
  /** 提示文字颜色 */
  hintColor?: string;
  /** 卡片背景色 */
  cardBackground?: string;
  /** 卡片边框色 */
  cardBorderColor?: string;
  /** 滚动指示器主色 */
  indicatorColor?: string;
  /** 视差圆圈的颜色列表（按索引循环使用） */
  parallaxColors?: string[];
  /** 视差圆圈数量 */
  parallaxCount?: number;
  /** 滚动动画结束的帧数（frame 映射到 scrollProgress 1） */
  scrollEndFrame?: number;
  /** 标题字号 */
  titleFontSize?: number;
  /** 副标题字号 */
  subtitleFontSize?: number;
  /** 卡片标题字号 */
  cardTitleFontSize?: number;
  /** 卡片描述字号 */
  cardDescFontSize?: number;
  /** 卡片组距顶部距离 */
  cardsTopOffset?: number;
  /** 卡片间距 */
  cardGap?: number;
  /** 卡片组宽度（百分比字符串或数字 px） */
  cardGroupWidth?: string | number;
  /** 卡片内边距 */
  cardPadding?: string;
  /** 卡片左右交错偏移量（px） */
  cardStaggerOffset?: number;
  /** 卡片展开动画时长（帧） */
  cardRevealDuration?: number;
  /** 滚动指示器淡出起始帧 */
  indicatorFadeStart?: number;
  /** 滚动指示器淡出结束帧 */
  indicatorFadeEnd?: number;
  /** Spring 物理动画参数 */
  springConfig?: {
    damping?: number;
    stiffness?: number;
    mass?: number;
  };
};

const DEFAULT_CARDS: ScrollDrivenCard[] = [
  { title: 'Scroll-Timeline', desc: '2026 CSS 新特性', delay: 10 },
  { title: 'View-Timeline', desc: '视口驱动动画', delay: 20 },
  { title: 'Animation-Timeline', desc: '时间轴动画', delay: 30 },
  { title: 'Scroll-Driven', desc: '性能优化', delay: 40 },
];

const DEFAULT_PARALLAX_COLORS = [
  'rgba(245,158,11,0.2)',
  'rgba(6,182,212,0.2)',
];

export const ScrollDrivenAnimation: React.FC<ScrollDrivenAnimationProps> = ({
  title = 'CSS Scroll-Driven',
  subtitle = '2026 CSS 新特性',
  cards = DEFAULT_CARDS,
  scrollHintText = '模拟滚动',
  background = 'linear-gradient(180deg, #0a0a0a 0%, #1a1a1a 50%, #0a0a0a 100%)',
  progressBarGradient = 'linear-gradient(90deg, #f59e0b, #06b6d4)',
  titleGradient = 'linear-gradient(90deg, #f59e0b, #06b6d4, #f59e0b)',
  primaryColor = '#fff',
  secondaryColor = '#999',
  hintColor = '#666',
  cardBackground = 'rgba(255,255,255,0.05)',
  cardBorderColor = 'rgba(255,255,255,0.1)',
  indicatorColor = '#f59e0b',
  parallaxColors = DEFAULT_PARALLAX_COLORS,
  parallaxCount = 5,
  scrollEndFrame = 150,
  titleFontSize = 70,
  subtitleFontSize = 34,
  cardTitleFontSize = 32,
  cardDescFontSize = 24,
  cardsTopOffset = 280,
  cardGap = 24,
  cardGroupWidth = '80%',
  cardPadding = '24px 32px',
  cardStaggerOffset = 50,
  cardRevealDuration = 30,
  indicatorFadeStart = 120,
  indicatorFadeEnd = 150,
  springConfig,
}) => {
  const frame = useCurrentFrame();
  const { fps } = useVideoConfig();

  const mergedSpringConfig = {
    damping: springConfig?.damping ?? 20,
    stiffness: springConfig?.stiffness ?? 100,
    mass: springConfig?.mass ?? 0.5,
  };

  const scrollProgress = interpolate(frame, [0, scrollEndFrame], [0, 1]);

  const titleOpacity = spring({ frame, fps, config: mergedSpringConfig });
  const titleY = interpolate(titleOpacity, [0, 1], [100, 0]);

  return (
    <AbsoluteFill
      style={{
        background,
        fontFamily: 'Arial, sans-serif',
        overflow: 'hidden',
      }}
    >
      {/* 背景网格 */}
      <div
        style={{
          position: 'absolute',
          width: '100%',
          height: '100%',
          backgroundImage: `
            linear-gradient(rgba(255,255,255,0.03) 1px, transparent 1px),
            linear-gradient(90deg, rgba(255,255,255,0.03) 1px, transparent 1px)
          `,
          backgroundSize: '50px 50px',
          transform: `translateY(${interpolate(scrollProgress, [0, 1], [0, -100])}px)`,
        }}
      />

      {/* 进度条 */}
      <div
        style={{
          position: 'absolute',
          top: 20,
          left: '50%',
          transform: 'translateX(-50%)',
          width: '80%',
          height: 6,
          background: 'rgba(255,255,255,0.1)',
          borderRadius: 3,
          overflow: 'hidden',
        }}
      >
        <div
          style={{
            width: `${scrollProgress * 100}%`,
            height: '100%',
            background: progressBarGradient,
            borderRadius: 3,
          }}
        />
      </div>

      {/* 主标题 */}
      <div
        style={{
          position: 'absolute',
          top: 80,
          left: '50%',
          transform: `translateX(-50%) translateY(${titleY}px)`,
          opacity: titleOpacity,
          textAlign: 'center',
          width: '100%',
        }}
      >
        <h1
          style={{
            fontSize: titleFontSize,
            fontWeight: 'bold',
            background: titleGradient,
            backgroundSize: '200% 100%',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            margin: 0,
            animation: 'gradient-shift 3s ease infinite',
          }}
        >
          {title}
        </h1>
        <p
          style={{
            fontSize: subtitleFontSize,
            color: secondaryColor,
            marginTop: 16,
          }}
        >
          {subtitle}
        </p>
      </div>

      {/* 卡片列表 */}
      <div
        style={{
          position: 'absolute',
          top: cardsTopOffset,
          left: '50%',
          transform: 'translateX(-50%)',
          display: 'flex',
          flexDirection: 'column',
          gap: cardGap,
          width: cardGroupWidth,
        }}
      >
        {cards.map((card, index) => {
          const cardSpring = spring({
            frame: frame - card.delay,
            fps,
            config: mergedSpringConfig,
          });

          return (
            <div
              key={index}
              style={{
                opacity: cardSpring,
                transform: `
                  translateY(${interpolate(cardSpring, [0, 1], [50, 0])}px)
                  translateX(${index % 2 === 0 ? -cardStaggerOffset : cardStaggerOffset}px)
                `,
                background: cardBackground,
                backdropFilter: 'blur(10px)',
                padding: cardPadding,
                borderRadius: 12,
                border: `1px solid ${cardBorderColor}`,
                boxShadow: '0 4px 16px rgba(0,0,0,0.3)',
              }}
            >
              <h3
                style={{
                  fontSize: cardTitleFontSize,
                  color: primaryColor,
                  margin: 0,
                  marginBottom: 8,
                }}
              >
                {card.title}
              </h3>
              <p
                style={{
                  fontSize: cardDescFontSize,
                  color: secondaryColor,
                  margin: 0,
                }}
              >
                {card.desc}
              </p>
            </div>
          );
        })}
      </div>

      {/* 视差背景元素 */}
      {Array.from({ length: parallaxCount }).map((_, i) => (
        <div
          key={i}
          style={{
            position: 'absolute',
            left: `${20 + i * 15}%`,
            top: `${20 + i * 10}%`,
            width: 100 + i * 20,
            height: 100 + i * 20,
            borderRadius: '50%',
            background: `radial-gradient(circle, ${
              parallaxColors[i % parallaxColors.length]
            } 0%, transparent 70%)`,
            transform: `translateY(${interpolate(
              scrollProgress,
              [0, 1],
              [0, -200 - i * 50]
            )}px)`,
          }}
        />
      ))}

      {/* 滚动指示器 */}
      <div
        style={{
          position: 'absolute',
          bottom: 40,
          left: '50%',
          transform: 'translateX(-50%)',
          textAlign: 'center',
          opacity: interpolate(
            frame,
            [indicatorFadeStart, indicatorFadeEnd],
            [1, 0],
            { extrapolateLeft: 'clamp', extrapolateRight: 'clamp' }
          ),
        }}
      >
        <div
          style={{
            width: 24,
            height: 40,
            border: '2px solid rgba(255,255,255,0.3)',
            borderRadius: 12,
            margin: '0 auto 8px',
            position: 'relative',
          }}
        >
          <div
            style={{
              position: 'absolute',
              top: 8,
              left: '50%',
              transform: 'translateX(-50%)',
              width: 4,
              height: 8,
              background: indicatorColor,
              borderRadius: 2,
              animation: 'scroll-indicator 1s ease-in-out infinite',
            }}
          />
        </div>
        <p style={{ color: hintColor, fontSize: 14, margin: 0 }}>{scrollHintText}</p>
      </div>

      {/* CSS 动画 */}
      <style>
        {`
          @keyframes gradient-shift {
            0%, 100% { background-position: 0% 50%; }
            50% { background-position: 100% 50%; }
          }
          @keyframes scroll-indicator {
            0%, 100% { top: 8px; opacity: 1; }
            50% { top: 20px; opacity: 0.3; }
          }
        `}
      </style>
    </AbsoluteFill>
  );
};
